/**
 * Face ID sign-in in the iPhone app (and fingerprint / face unlock in the Android app). Turning it on gives this phone a random key from the server
 * (POST /api/me/device-keys); the key goes into the iOS keychain locked by Face ID ("biometry current
 * set": a new face or fingerprint enrolled on the phone voids it). Opening the app while signed out then
 * asks for Face ID by itself and signs in with the key — no password, no extra Apple sheet. The server
 * keeps only a hash; sign-out everywhere revokes every phone's key.
 */
import { api, ApiError } from './api.ts';
import { isNative, nativePlatform } from './native.ts';

const SERVER = 'chat.tightav.com';
const KEY_ID = 'tc.faceIdKey';
const OFFER = 'tc.faceIdOffer';

const plugin = () => import('@capgo/capacitor-native-biometric');

export type FaceIdKind = 'face' | 'touch' | 'android' | null;
const phone = () => isNative && (nativePlatform === 'ios' || nativePlatform === 'android');

/** Face ID / Touch ID (iPhone) or fingerprint / face unlock (Android) set up on this phone, inside the app. */
export async function faceIdKind(): Promise<FaceIdKind> {
  if (!phone()) return null;
  try {
    const { NativeBiometric, BiometryType } = await plugin();
    const r = await NativeBiometric.isAvailable();
    if (!r.isAvailable) return null;
    if (nativePlatform === 'android') return 'android';
    return r.biometryType === BiometryType.TOUCH_ID ? 'touch' : 'face';
  } catch {
    return null;
  }
}

export async function faceIdOn(): Promise<boolean> {
  if (!phone()) return false;
  try {
    const { NativeBiometric } = await plugin();
    return (await NativeBiometric.isCredentialsSaved({ server: SERVER })).isSaved;
  } catch {
    return false;
  }
}

function store(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}
function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export async function enableFaceId(): Promise<void> {
  const { NativeBiometric, AccessControl } = await plugin();
  // Ask for Face ID once now, so the person sees it work (and iOS asks for permission the first time).
  await NativeBiometric.verifyIdentity({ reason: 'T-Lián', title: 'T-Lián' });
  const platform = nativePlatform === 'android' ? 'android' : 'ios';
  const k = await api<{ id: string; token: string }>('POST', '/api/me/device-keys', { platform, name: platform === 'ios' ? 'iPhone' : 'Android' });
  await NativeBiometric.setCredentials({
    username: 'tlian',
    password: k.token,
    server: SERVER,
    accessControl: AccessControl.BIOMETRY_CURRENT_SET,
    title: 'T-Lián',
    negativeButtonText: '×',
  });
  store(KEY_ID, k.id);
  store(OFFER, 'done');
}

export async function disableFaceId(): Promise<void> {
  const { NativeBiometric } = await plugin();
  const id = read(KEY_ID);
  if (id) await api('POST', '/api/me/device-keys/remove', { id }).catch(() => {});
  await NativeBiometric.deleteCredentials({ server: SERVER }).catch(() => {});
  store(KEY_ID, null);
}

/**
 * Face ID → the key from the keychain → a session. Throws 'cancelled' if the person looks away or taps
 * Cancel, 'revoked' if the server no longer knows the key (then it is removed from the phone too).
 */
export async function signInWithFaceId(): Promise<void> {
  const { NativeBiometric } = await plugin();
  let token: string;
  try {
    token = (await NativeBiometric.getSecureCredentials({ server: SERVER, reason: 'T-Lián', title: 'T-Lián', negativeButtonText: '×' })).password;
  } catch {
    throw new Error('cancelled');
  }
  try {
    await api('POST', '/api/auth/device-login', { token });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      await NativeBiometric.deleteCredentials({ server: SERVER }).catch(() => {});
      store(KEY_ID, null);
      throw new Error('revoked');
    }
    throw err;
  }
}

/** After a password sign-in in the iPhone app: offer Face ID once (until accepted or declined). */
export function shouldOfferFaceId(): boolean {
  return read(OFFER) === null;
}
export function declineFaceIdOffer(): void {
  store(OFFER, 'declined');
}

/*
 * App lock (phones only): with Face ID / fingerprint on, the app locks when it has been in the background
 * longer than the chosen time, and Face ID / fingerprint opens it again. The session stays signed in behind
 * the lock (notifications keep coming). Computers never lock.
 */
const LOCK_AFTER = 'tc.lockAfter';
const HIDDEN_AT = 'tc.hiddenAt';
/** Minutes in the background before the app locks; 0 = at once, -1 = never. */
export const LOCK_CHOICES = [0, 1, 5, 15, 30, 60, -1] as const;
export const LOCK_DEFAULT = 5;

export function lockAfterMinutes(): number {
  const v = read(LOCK_AFTER);
  const n = v === null ? LOCK_DEFAULT : Number(v);
  return Number.isFinite(n) ? n : LOCK_DEFAULT;
}
export function setLockAfterMinutes(minutes: number): void {
  store(LOCK_AFTER, String(minutes));
}
/** The app went to the background (or was closed). */
export function noteHidden(): void {
  store(HIDDEN_AT, String(Date.now()));
}
/** Unlocked or signed in: the clock starts again. */
export function clearHidden(): void {
  store(HIDDEN_AT, null);
}
/** Has the app been away long enough to lock? (Phones with the lock on only; Face ID itself is checked separately.) */
export function lockDue(now = Date.now()): boolean {
  if (!phone()) return false;
  const minutes = lockAfterMinutes();
  if (minutes < 0) return false;
  const at = Number(read(HIDDEN_AT));
  if (!at) return false;
  return now - at >= minutes * 60_000;
}
/** Face ID / fingerprint check for opening the locked app; false if cancelled or not recognised. */
export async function unlockWithBiometrics(): Promise<boolean> {
  try {
    const { NativeBiometric } = await plugin();
    await NativeBiometric.verifyIdentity({ reason: 'T-Lián', title: 'T-Lián', negativeButtonText: '×' });
    return true;
  } catch {
    return false;
  }
}
